import { ISupplier, IForm, ISupplierAddress, ISupplierModel } from './types';
import { httpService } from '../../services/http.service';
import { BASE_URL } from '../../constants/endpoints';
import { useStore } from '../../store';
import { VIEWS_MAP } from '../../router/constants';
import { STEPS } from '../MailCheck/consts';
import { DYNAMIC_FORM_TYPES } from '../../components/form/dynamicForm/types';
import { SUPPLIER_REFERRER_TYPES } from './consts';
import { FORM_TYPES } from './manageComponents/FormTypeSwitch';
import { SupplierEntityTypes } from '../../components/VendorManager/interfaces';

export const getInitialCreationPayload = (): ISupplier => ({
  supplierEntityType: '',
  name: '',
  supplierAddressData: {
    city: '',
    state: '',
    line1: '',
    line2: '',
    postalCode: 0,
    country: '',
  },
});

export const mapPayloadValues = (payload: ISupplier, formData: IForm, formType: string) => {
  const updatedPayload = { ...payload };
  updatedPayload.name = formData.businessName;
  updatedPayload.supplierEntityType = formType;
  updatedPayload.supplierAddressData.city = formData.city;
  updatedPayload.supplierAddressData.line1 = formData.lineOne;
  updatedPayload.supplierAddressData.line2 = formData.lineTwo;
  updatedPayload.supplierAddressData.state = formData.state;
  updatedPayload.supplierAddressData.postalCode = formData.zipCode;
  return updatedPayload;
};

const validateAddressObject = (formData: any) => {
  const addressParts = [
    formData?.city,
    formData?.state,
    formData?.lineOne,
    formData?.lineTwo,
    formData?.zipCode,
    formData?.country,
  ];
  const isSomePartFilled = addressParts.some((part) => part);
  if (isSomePartFilled) {
    return {
      city: formData?.city || null,
      state: formData?.state || null,
      line1: formData?.lineOne || null,
      line2: formData?.lineTwo || null,
      postalCode: formData?.zipCode || null,
      country: formData?.country || null,
    };
  }
  return null;
};

export const generateUpdateSupplierPayload = (
  data: any,
  supplierId: string,
  activeFormType: string,
  methodType?: string,
) => ({
  payload: {
    supplierId: supplierId || null,
    supplierEntityType: activeFormType === FORM_TYPES.BUSINESS ? SupplierEntityTypes.BUSINESS : SupplierEntityTypes.INDIVIDUAL,
    methodType: methodType || null,
    name: data.businessName,
    email: data.email || null,
    wireAccountData:
      data.wireAccountNumber && data.wireRoutingNumber
        ? {
          accountNumber: data.wireAccountNumber,
          routingNumber: data.wireRoutingNumber,
        }
        : null,
    achAccountData: {
      accountNumber: data.achAccountNumber || null,
      routingNumber: data.achRoutingNumber || null,
    },
    supplierAddressData: validateAddressObject(data),
    notifyOnPayment: data.notifyOnPayment || null,
  },
});

export const getInitials = (name: string) => {
  const splitWords = name.split(' ');
  return `${splitWords[0][0]} ${splitWords?.[1]?.[0] || ''}`.toUpperCase();
};

export const getAddressDetails = (addressObj: any) => {
  if (!addressObj?.line1 || !addressObj) return '';
  return `${addressObj.line1}, ${addressObj.line2 ? `${addressObj.line2},` : ''} ${addressObj.city}, ${addressObj.state} ${addressObj.postalCode}`;
};

export const getAddressDetailsHtml = (addressObj: ISupplierAddress) =>
  `${addressObj.line1}${addressObj.line2 ? `, ${addressObj.line2} </br>` : '</br>'} ${addressObj.city}, ${addressObj.state}, ${addressObj.postalCode}`;

export const getProfileColorId = (userId: string): number => {
  let sum = 0;
  for (let i = 0; i < userId.length; i++) {
    sum += userId.charCodeAt(i);
  }
  return sum % 9;
};

export const getSupplierDataById = async (supplierExternalId: string) => {
  const {
    user: { customerId },
    getPersonalAccount,
  } = useStore.getState();
  const { accountExternalId } = getPersonalAccount();
  const res = await httpService.get(
    `${BASE_URL}/api/v1/user/${customerId}/${accountExternalId}/supplier/${supplierExternalId}`,
  );
  return res.data.supplier as ISupplierModel;
};

export const handleSubmitCallbackByContext = ({
  context,
  supplierData,
  navigate,
  id,
  transferAmount,
}: {
  context: string;
  supplierData?: ISupplierModel;
  navigate: any;
  id?: string;
  transferAmount?: string;
}) => {
  const { setUpperMessageContent } = useStore.getState();
  if (context === DYNAMIC_FORM_TYPES.MAIL_A_CHECK) {
    setUpperMessageContent(`<b> ${supplierData?.name} </b> updated`);
    navigate(`/${VIEWS_MAP.MAIL_A_CHECK}`, {
      state: { redirectStep: id ? STEPS.REVIEW : STEPS.AMOUNT, payeeDetails: supplierData },
    });
  } else if (context === DYNAMIC_FORM_TYPES.ACH_OUT) {
    if (!id) {
      navigate(`/${VIEWS_MAP}`, { replace: true, state: { payeeDetails: supplierData, transferAmount } });
    } else {
      navigate(`/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.PICKER}`, {
        replace: true,
        state: {
          payeeDetails: supplierData,
          referrer: SUPPLIER_REFERRER_TYPES.ACH_OUT,
          closeUrl: `/${VIEWS_MAP.DASHBOARD}`,
        },
      });
    }
  } else if (context === DYNAMIC_FORM_TYPES.ACH_OUT_MANDATORY) {
    navigate(`/${VIEWS_MAP}`, { replace: true, state: { payeeDetails: supplierData, transferAmount } });
  }
};

export const handleDeleteCallbackByContext = ({ context }: { context: string }) => {
  if (context === DYNAMIC_FORM_TYPES.MAIL_A_CHECK) {
    const { resetMailCheckSlice } = useStore.getState();
    resetMailCheckSlice();
  }
};

export const handleBackCallbackByContext = ({
  context,
  supplierData,
  navigate,
  id,
  isNativeBack,
}: {
  context: string;
  supplierData?: ISupplierModel;
  navigate: any;
  id?: string;
  isNativeBack?: boolean;
}) => {
  if (context === DYNAMIC_FORM_TYPES.MAIL_A_CHECK) {
    if (id && !isNativeBack) {
      navigate(`/${VIEWS_MAP.MAIL_A_CHECK}`, { state: { redirectStep: STEPS.REVIEW, payeeDetails: supplierData } });
    } else {
      navigate(`/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.PICKER}`, {
        state: {
          redirectStep: STEPS.AMOUNT,
          payeeDetails: supplierData,
          referrer: context,
        },
      });
    }
  } else if (context === DYNAMIC_FORM_TYPES.ACH_OUT) {
    navigate(`/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.PICKER}`, {
      state: {
        payeeDetails: null,
        referrer: context,
      },
    });
  } else if (context === DYNAMIC_FORM_TYPES.ACH_OUT_MANDATORY) {
    navigate(`/${VIEWS_MAP.SUPPLIER.BASE}/${VIEWS_MAP.SUPPLIER.PICKER}`, {
      state: {
        payeeDetails: supplierData,
        referrer: context,
      },
    });
  }
};
